import {
  challengeRepository,
  dailyScoreLedgerRepository,
  habitRepository,
  logRepository,
  progressStateRepository,
  vacationAllowanceRepository,
} from '../db/database';
import {
  INITIAL_BAR_VALUE,
  SINGLETON_ID,
  type IsoDate,
  type ProgressState,
} from '../models/types';
import { addDays, daysBetween, todayIso } from '../utils/date';
import { syncAutomaticCleaningLog } from './cleaning';
import { getDayCover } from './dayCover';
import { grantVacationOnMilestone, useVacationDay } from './vacation';
import {
  applyDeltaToBar,
  applyStreakResult,
  calculateDailyGrossDelta,
  finalizeDailyDelta,
  isDayStreakEligible,
} from './scoring';

/**
 * Gün kapanışı (rollover).
 *
 * `scoring.ts` saf hesap yapar; bu dosya o hesabı veritabanıyla
 * birleştirir: geçmiş günleri kapatır, `dailyScoreLedger`'a
 * değiştirilemez kayıtları yazar ve `ProgressState`'i günceller.
 *
 * Ne zaman çalışır: uygulama her açıldığında (bkz. `main.ts`).
 * Gece yarısı arka planda çalışan bir zamanlayıcı YOKTUR — bir PWA
 * kapalıyken kod çalıştıramaz. Bunun yerine "en son hangi günü
 * kapattık?" bilgisini saklayıp, aradaki tüm günleri açılışta sırayla
 * kapatıyoruz (§7.2).
 */

/**
 * Tek seferde kapatılacak en fazla gün sayısı — güvenlik sınırı.
 *
 * Normalde bu sınıra hiç yaklaşılmaz (kullanıcı uygulamayı aylarca
 * açmazsa bile). Asıl amacı, cihazın saati yanlışlıkla çok ileri bir
 * tarihe alınırsa binlerce cezalı günün yazılıp barı sıfırlamasını
 * engellemek.
 */
const MAX_DAYS_TO_FINALIZE = 400;

/** Yeni kurulumda kullanılacak başlangıç durumu (§7.2). */
function createInitialProgressState(today: IsoDate): ProgressState {
  return {
    id: SINGLETON_ID,
    barValue: INITIAL_BAR_VALUE,
    currentStreak: 0,
    longestStreak: 0,
    // Bugünden ÖNCEKİ günler hiç kapatılmayacak: uygulama o günlerde
    // kullanılmıyordu, kullanıcıyı kullanmadığı günler için
    // cezalandırmak haksız olurdu. Bu yüzden "son kapanan gün" olarak
    // dünü işaretliyoruz; ilk kapanacak gerçek gün bugün olacak.
    lastFinalizedDate: addDays(today, -1),
    // Henüz hiçbir gün ödülle satın alınmadı (§7.6).
    rewardDates: [],
  };
}

/**
 * Mevcut ilerleme durumunu getirir; hiç yoksa oluşturup kaydeder.
 * Uygulamanın her yerinde `ProgressState`'e buradan erişilmeli.
 */
export async function getOrCreateProgressState(): Promise<ProgressState> {
  const existing = await progressStateRepository.get();
  if (existing) {
    return existing;
  }

  const initial = createInitialProgressState(todayIso());
  await progressStateRepository.upsert(initial);
  return initial;
}

/**
 * Belirli bir gün dondurulmuş muydu? — tatil (§7.7) ya da ödül (§7.6).
 *
 * Ekranlar bunu "bugün güvencede mi?" sorusu için kullanır; gün
 * kapanışı ise `getDayCover`'ı doğrudan çağırır çünkü hangi kaynağın
 * dondurduğunu da bilmesi gerekir.
 */
export async function isFrozenDay(date: IsoDate): Promise<boolean> {
  const [allowance, progress] = await Promise.all([
    vacationAllowanceRepository.get(),
    getOrCreateProgressState(),
  ]);
  return getDayCover(date, allowance, progress) !== 'none';
}

/**
 * Belirli bir günde challenge tamamlanmış mıydı? (§7.3)
 *
 * İptal edilmiş veya bitmiş challenge'lar da taranır: o gün fiilen
 * tamamlanmışsa, challenge sonradan iptal edilse bile geçmiş günün
 * streak hakkı geri alınmaz.
 */
export async function isChallengeCompletedOn(date: IsoDate): Promise<boolean> {
  const challenges = await challengeRepository.list();
  return challenges.some((challenge) =>
    challenge.completedDates.includes(date),
  );
}

/**
 * TEK bir günü kapatır: ham toplamı hesaplar, tam sayıya yuvarlar,
 * puan defterine değiştirilemez kaydı yazar ve yeni durumu döndürür.
 *
 * Veritabanına yazar ama `ProgressState`'i KAYDETMEZ — güncellenmiş
 * durumu döndürür. Böylece birden fazla gün kapatılırken her adımda
 * ayrı yazma yapılmaz, en sonda tek sefer kaydedilir.
 */
async function finalizeSingleDay(
  date: IsoDate,
  state: ProgressState,
): Promise<ProgressState> {
  // §7.5 — Otomatik "Temizlik" alışkanlığının o güne ait kaydını, gün
  // kapanmadan HEMEN ÖNCE yaz. Sıra önemli: kayıt yazıldıktan sonra
  // okunmalı ki aşağıdaki hesap onu da görsün.
  await syncAutomaticCleaningLog(date);

  const [dayLogs, allHabits, allowance, challengeCompletedToday] =
    await Promise.all([
      logRepository.listByDate(date),
      habitRepository.list(),
      vacationAllowanceRepository.get(),
      isChallengeCompletedOn(date),
    ]);

  // --- Günün güvencesi (§7.6 / §7.7) -------------------------------
  let cover = getDayCover(date, allowance, state);

  // Gün kendi başına seriye sayılır mıydı? Otomatik tatil kararından
  // ÖNCE, dondurulmamış hâliyle sorulmalı — aksi halde soru kendi
  // cevabına dayanırdı.
  const naturallyEligible = isDayStreakEligible(dayLogs, allHabits, {
    date,
    isFrozenDay: false,
    challengeCompletedToday,
  });

  // **KESİNLEŞTİ — Otomatik tatil (§7.7):** Gün başarısızsa ve elde
  // kullanılmamış tatil hakkı varsa, hak KENDİLİĞİNDEN harcanır ve gün
  // tatil günü olarak kaydedilir. Kullanıcı seriyi kaybetmesin diye
  // her gün uygulamayı açıp "Kullan" demek zorunda kalmamalı.
  //
  // Sınır YOK: uzun bir aradan sonra birden çok başarısız gün
  // kapanıyorsa, hak bittiği ana kadar her biri için harcanır.
  if (cover === 'none' && !naturallyEligible) {
    const result = await useVacationDay(date);
    if (result.ok) {
      cover = 'vacation';
    }
  }

  // Yerel değişken adı `frozen`: aynı modüldeki `isFrozenDay`
  // fonksiyonunu gölgelememesi için.
  const frozen = cover !== 'none';

  // --- Başarı Barı (§7.2) ------------------------------------------
  const grossDelta = calculateDailyGrossDelta(dayLogs, allHabits, {
    date,
    isFrozenDay: frozen,
  });
  const finalDelta = finalizeDailyDelta(grossDelta);
  const barValueAfter = applyDeltaToBar(state.barValue, finalDelta);

  // --- Day Streak (§7.3) -------------------------------------------
  // Dondurulmuş gün her hâlükârda sayılır; değilse doğal sonuç geçerli.
  const isEligible = frozen || naturallyEligible;
  const streak = applyStreakResult(state, isEligible);

  // Defter kaydı, streak kararı da verildikten SONRA yazılıyor:
  // uygunluk sonradan yeniden hesaplanamadığı için aynı kayda
  // dondurulması gerekiyor (bkz. `DailyScoreLedger.wasStreakEligible`).
  await dailyScoreLedgerRepository.append({
    date,
    grossDelta,
    finalDelta,
    barValueAfter,
    wasVacationDay: cover === 'vacation',
    wasRewardDay: cover === 'reward',
    wasStreakEligible: isEligible,
  });

  // §7.7 — Streak bir 90 katına ulaştıysa tatil hakkını tamamla.
  // Streak güncellendikten HEMEN SONRA çalışmalı: kontrol, günün yeni
  // seri değerine bakıyor.
  await grantVacationOnMilestone(streak.currentStreak, date);

  return {
    ...state,
    barValue: barValueAfter,
    currentStreak: streak.currentStreak,
    longestStreak: streak.longestStreak,
    lastFinalizedDate: date,
  };
}

export interface DayRolloverResult {
  /** Bu çalıştırmada kapatılan günler (kronolojik sırada). */
  finalizedDates: IsoDate[];
  /** Kapanışlardan sonraki güncel durum. */
  state: ProgressState;
}

/**
 * Kapatılmamış tüm geçmiş günleri sırayla kapatır.
 *
 * BUGÜN kapatılmaz — gün henüz bitmedi, kullanıcı hâlâ alışkanlık
 * işaretleyebilir. Bugünün değişimi yalnızca canlı olarak gösterilir
 * (`calculateLiveScore`) ve ancak yarın uygulama açıldığında
 * kesinleşir.
 *
 * Günler eskiden yeniye doğru tek tek işlenir çünkü her günün
 * `barValueAfter` değeri bir öncekinin sonucuna dayanır.
 */
export async function runDayRollover(
  today: IsoDate = todayIso(),
): Promise<DayRolloverResult> {
  let state = await getOrCreateProgressState();
  const finalizedDates: IsoDate[] = [];

  // İlk kurulumda `createInitialProgressState` zaten dünü işaretler,
  // ama kayıt bozulmuşsa diye burada da güvenceye alıyoruz.
  if (state.lastFinalizedDate === null) {
    state = { ...state, lastFinalizedDate: addDays(today, -1) };
    await progressStateRepository.upsert(state);
    return { finalizedDates, state };
  }

  // Döngü boyunca ilerleyen imleç. Ayrı bir değişkende tutuyoruz çünkü
  // `state` her adımda yeniden atanıyor ve TypeScript yukarıdaki null
  // kontrolünü o noktada artık hatırlamıyor.
  let cursor: IsoDate = state.lastFinalizedDate;

  // Kapatılacak gün sayısı: son kapanan günden dünü de dahil ederek
  // bugüne kadar. `daysBetween` bugünü de sayar, o yüzden 1 çıkarıyoruz.
  const pendingDayCount = daysBetween(cursor, today) - 1;

  // Cihaz saati geriye alınmışsa (veya hiç gün geçmemişse) yapacak
  // bir şey yok.
  if (pendingDayCount <= 0) {
    return { finalizedDates, state };
  }

  // Saat çok ileri alınmışsa, aradaki günleri kapatmadan atlıyoruz —
  // yüzlerce sahte cezalı gün yazmaktansa hiç yazmamak daha güvenli.
  if (pendingDayCount > MAX_DAYS_TO_FINALIZE) {
    console.warn(
      `Son kapanan günden bu yana ${pendingDayCount} gün geçmiş; ` +
        `güvenlik sınırı (${MAX_DAYS_TO_FINALIZE}) aşıldığı için bu günler ` +
        'puan defterine yazılmadan atlandı.',
    );
    state = { ...state, lastFinalizedDate: addDays(today, -1) };
    await progressStateRepository.upsert(state);
    return { finalizedDates, state };
  }

  for (let offset = 1; offset <= pendingDayCount; offset += 1) {
    const date = addDays(cursor, 1);
    cursor = date;

    // Aynı gün için kayıt zaten varsa (beklenmedik bir durum) tekrar
    // yazmayı denemiyoruz — `append` hata fırlatır ve açılışı kilitlerdi.
    const existing = await dailyScoreLedgerRepository.get(date);
    if (existing) {
      state = { ...state, lastFinalizedDate: date };
      continue;
    }

    state = await finalizeSingleDay(date, state);
    finalizedDates.push(date);
  }

  await progressStateRepository.upsert(state);
  return { finalizedDates, state };
}
